import * as Icons from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { type BaseComponentProps } from "../../types/puck";
import { SmartLink } from "../smart-link";

export type ServicesVariant = "minimal" | "cards" | "feature";

export interface ServiceItem {
  title: string;
  description: string;
  icon?: string;
  href?: string;
}

export interface ServicesProps extends BaseComponentProps {
  heading: string;
  subheading?: string;
  services: ServiceItem[];
  layout: "grid" | "list";
  columns: "2" | "3" | "4";
  variant?: ServicesVariant;
  showIcons: "yes" | "no";
  backgroundColor?: string;
}

function getGridCols(columns: ServicesProps["columns"]): string {
  switch (columns) {
    case "2":
      return "jobsite:md:grid-cols-2";
    case "3":
      return "jobsite:md:grid-cols-2 jobsite:xl:grid-cols-3";
    case "4":
      return "jobsite:md:grid-cols-2 jobsite:xl:grid-cols-4";
    default:
      return "jobsite:md:grid-cols-2 jobsite:xl:grid-cols-3";
  }
}

export const Services: React.FC<ServicesProps> = ({
  heading,
  subheading,
  services: servicesProp,
  layout = "grid",
  columns = "3",
  variant = "cards",
  showIcons = "yes",
  backgroundColor,
  className,
}) => {
  const services = Array.isArray(servicesProp) ? servicesProp : [];
  const surfaceRadiusStyle = { borderRadius: "var(--jobsite-radius)" } as const;

  const renderIcon = (iconValue?: string) => {
    const fallbackIcon = (
      <Icons.BadgeCheck className="jobsite:h-full jobsite:w-full" strokeWidth={1.5} />
    );

    if (!iconValue) return fallbackIcon;

    const IconComponent = (Icons as unknown as Record<string, LucideIcon>)[iconValue];

    if (!IconComponent) {
      return <Icons.Settings className="jobsite:h-full jobsite:w-full" strokeWidth={1.5} />;
    }

    return <IconComponent className="jobsite:h-full jobsite:w-full" strokeWidth={1.5} />;
  };

  const renderServiceTitle = (service: ServiceItem) => {
    if (!service.href) return service.title;

    return (
      <SmartLink to={service.href} className="jobsite:underline-offset-2 jobsite:hover:underline">
        {service.title}
      </SmartLink>
    );
  };

  const renderServiceItem = (service: ServiceItem, idx: number) => {
    const isGrid = layout === "grid";
    const cardClass = cn(
      "jobsite:group jobsite:h-full",
      variant === "minimal" && isGrid && "jobsite:text-center",
      variant === "minimal" &&
        !isGrid &&
        "jobsite:flex jobsite:gap-4 jobsite:border-b jobsite:border-border/70 jobsite:pb-5 jobsite:last:border-b-0",
      variant === "cards" &&
        "jobsite:border jobsite:border-border/70 jobsite:bg-background jobsite:p-6 jobsite:shadow-sm jobsite:transition-all jobsite:duration-200 jobsite:hover:-translate-y-0.5 jobsite:hover:border-primary/20 jobsite:hover:shadow-md",
      variant === "feature" &&
        "jobsite:border jobsite:border-border/50 jobsite:bg-linear-to-b jobsite:from-background jobsite:to-muted/30 jobsite:p-6 jobsite:shadow-sm",
      variant === "feature" && !isGrid && "jobsite:border-l-4 jobsite:border-primary jobsite:pl-5",
    );
    const iconWrapClass = cn(
      "jobsite:flex jobsite:items-center jobsite:justify-center jobsite:shrink-0",
      variant === "minimal" &&
        "jobsite:mx-auto jobsite:mb-4 jobsite:h-12 jobsite:w-12 jobsite:rounded-full jobsite:bg-accent/15 jobsite:text-accent",
      variant === "cards" &&
        "jobsite:mb-5 jobsite:h-12 jobsite:w-12 jobsite:rounded-2xl jobsite:bg-primary/10 jobsite:text-primary jobsite:transition-colors jobsite:duration-200 jobsite:group-hover:bg-primary jobsite:group-hover:text-primary-foreground",
      variant === "feature" &&
        "jobsite:mb-5 jobsite:h-12 jobsite:w-12 jobsite:rounded-2xl jobsite:bg-primary jobsite:text-primary-foreground",
      !isGrid && "jobsite:mx-0 jobsite:mb-0",
    );
    const titleClass = cn(
      "jobsite:font-heading jobsite:text-foreground",
      variant === "minimal"
        ? "jobsite:text-lg jobsite:font-semibold"
        : "jobsite:text-xl jobsite:font-semibold",
      variant === "cards" && "jobsite:group-hover:text-primary",
    );
    const bodyClass = cn(
      "jobsite:leading-relaxed jobsite:text-muted-foreground",
      variant === "minimal" ? "jobsite:text-sm" : "jobsite:text-base",
    );

    return (
      <div
        key={`${service.title}-${idx}`}
        className={cardClass}
        style={variant === "minimal" ? undefined : surfaceRadiusStyle}
      >
        <div
          className={cn(
            variant === "feature" &&
              isGrid &&
              "jobsite:mb-5 jobsite:flex jobsite:items-start jobsite:justify-between jobsite:gap-4",
          )}
        >
          {showIcons === "yes" && (
            <div className={iconWrapClass}>
              <div className="jobsite:h-5 jobsite:w-5">{renderIcon(service.icon)}</div>
            </div>
          )}

          {variant === "feature" && isGrid && (
            <span className="jobsite:text-xs jobsite:font-semibold jobsite:uppercase jobsite:tracking-[0.2em] jobsite:text-primary/60">
              {String(idx + 1).padStart(2, "0")}
            </span>
          )}
        </div>

        <div className={cn(!isGrid && "jobsite:flex-1")}>
          <h3 className={titleClass}>{renderServiceTitle(service)}</h3>
          <p className={cn("jobsite:mt-2", bodyClass)}>{service.description}</p>
        </div>
      </div>
    );
  };

  return (
    <section
      className={cn(
        "jobsite:py-16 jobsite:md:py-24",
        backgroundColor
          ? ""
          : variant === "feature"
            ? "jobsite:bg-muted/25"
            : "jobsite:bg-background",
        className,
      )}
      style={{
        backgroundColor: backgroundColor || undefined,
      }}
    >
      <div className="jobsite:container jobsite:mx-auto jobsite:px-4">
        <div className="jobsite:mx-auto jobsite:mb-12 jobsite:max-w-3xl jobsite:text-center">
          <h2 className="jobsite:font-heading jobsite:text-3xl jobsite:tracking-tight jobsite:text-balance jobsite:text-foreground jobsite:sm:text-4xl jobsite:md:text-5xl">
            {heading}
          </h2>
          {subheading && (
            <p className="jobsite:mt-4 jobsite:text-lg jobsite:text-pretty jobsite:text-muted-foreground jobsite:md:text-xl">
              {subheading}
            </p>
          )}
        </div>

        {layout === "grid" ? (
          <div className={cn("jobsite:grid jobsite:gap-5 jobsite:md:gap-6", getGridCols(columns))}>
            {services.map((service, idx) => renderServiceItem(service, idx))}
          </div>
        ) : (
          <div className="jobsite:mx-auto jobsite:max-w-4xl jobsite:space-y-4">
            {services.map((service, idx) => renderServiceItem(service, idx))}
          </div>
        )}
      </div>
    </section>
  );
};
